import {
  LocalAudioTrack,
  LocalVideoTrack,
  RemoteAudioTrack,
  RemoteParticipant,
  RemoteTrack,
  RemoteVideoTrack,
  Room
} from 'twilio-video';
import { AutomatedParticipantBase } from './AutomatedParticipantBase';
import type { RpcCallPayload, RpcCommandPayload } from '../../helpers/protocol/automatedParticipantProtocol';
const RECORDING_LABEL = 'automated-patient-recording';

type CanvasTrack = {
  track: LocalVideoTrack;
  video?: HTMLVideoElement;
  audioTrack?: LocalAudioTrack;
  stop: () => void;
};

/**
 * Automated clinician participant.
 * Handles call flow, video playback, and responds to patient interactions.
 */
export class AutomatedClinicianParticipant extends AutomatedParticipantBase {
  private remoteVideoTrack?: RemoteVideoTrack;
  private remoteAudioTrack?: RemoteAudioTrack;
  private remoteMediaStream?: MediaStream;
  private displayTrack?: LocalVideoTrack;
  private displayAudioTrack?: LocalAudioTrack;
  private displayCleanup?: () => void;
  private recordingPlaceholder?: CanvasTrack;
  private recorder?: MediaRecorder;
  private recordedChunks: Blob[] = [];
  private recordedBlob?: Blob;
  private recordingSourceElement?: HTMLVideoElement;
  private capturedStream?: MediaStream;
  private defaultOverlay?: CanvasTrack;
  private offerSent = false;

  /** Safely closes an audio context without throwing errors. */
  private async closeAudioContext(audioContext: AudioContext | null | undefined): Promise<void> {
    if (audioContext && audioContext.state !== 'closed') {
      try {
        await audioContext.close();
      } catch {}
    }
  }

  protected async onOfferToStart(_payload: RpcCallPayload): Promise<void> {
    // Clinician doesn't receive offers
  }

  protected onOfferToStartCancelled(_payload: RpcCallPayload): void {
    // Clinician doesn't receive offer cancellations
  }

  protected onCallAccepted(senderId: string, callId: string): void {
    this.logEvent({
      level: 'info',
      message: 'Clinician received call acceptance',
      context: { senderId, callId }
    }).catch(() => {});
  }

  protected override onRequestPromptVideo(senderId: string, data?: Record<string, unknown>): void {
    this.logEvent({
      level: 'info',
      message: 'Clinician received request to play prompt video',
      context: { senderId, data }
    }).catch(() => {});

    setTimeout(() => {
      this.playClip('https://assets.hbsuk.co.uk/video/prompt.mp4', 'clinician-prompt')
        .then((success) => {
          this.logEvent({
            level: 'info',
            message: 'Clinician video playback result',
            context: { success, url: 'https://assets.hbsuk.co.uk/video/prompt.mp4' }
          }).catch(() => {});

          if (success) {
            this.sendPromptVideoComplete({ clip: 'prompt' });
          }
        })
        .catch((error) => {
          this.logEvent({
            level: 'error',
            message: 'Clinician video playback failed',
            context: { error: error instanceof Error ? error.message : String(error) }
          }).catch(() => {});
        });
    }, 500);
  }

  protected async onStartRecording(_payload: RpcCommandPayload): Promise<void> {
    return this.startRecording();
  }

  protected async onStopRecording(_payload: RpcCommandPayload): Promise<void> {
    return this.stopRecording();
  }

  protected async onReplayRecording(_payload: RpcCommandPayload): Promise<void> {
    return this.replayRecording();
  }

  /** Shows thank you message and notifies clinician when complete. */
  protected async onEndRecordingSession(_payload: RpcCommandPayload): Promise<void> {
    try {
      const thankYouOverlay = this.createTextTrack(
        'Thank you. Please fill out the feedback form.',
        'thank-you-overlay'
      );
      await this.setDisplayTrack(thankYouOverlay.track, thankYouOverlay.stop);
      await new Promise((r) => setTimeout(r, 5000));
      await this.setDisplayTrack();
      Promise.resolve(this.sendThankYouVideoComplete({ clip: 'thank-you' })).catch(() => {});
    } catch (error) {
      await this.logEvent({
        level: 'error',
        message: 'Failed to show thank you message',
        context: { error: error instanceof Error ? error.message : String(error) }
      });
      Promise.resolve(this.sendThankYouVideoComplete({ clip: 'thank-you' })).catch(() => {});
    }
  }

  /** Sets up track listeners when clinician connects. */
  protected override onParticipantConnected(participant: RemoteParticipant): void {
    const trackSubscribed = (track: RemoteTrack) => this.handleRemoteTrackSubscribed(track);
    const trackUnsubscribed = (track: RemoteTrack) => this.handleRemoteTrackUnsubscribed(track);
    participant.on('trackSubscribed', trackSubscribed);
    participant.on('trackUnsubscribed', trackUnsubscribed);
    participant.tracks.forEach((publication) => {
      if (publication.track) {
        this.handleRemoteTrackSubscribed(publication.track);
      }
    });
  }

  /** Cleans up track listeners when clinician disconnects. */
  protected override onParticipantDisconnected(participant: RemoteParticipant): void {
    participant.removeAllListeners('trackSubscribed');
    participant.removeAllListeners('trackUnsubscribed');
    participant.tracks.forEach((publication) => {
      if (publication.track) {
        this.handleRemoteTrackUnsubscribed(publication.track);
      }
    });
  }

  private handleRemoteTrackSubscribed(track: RemoteTrack) {
    if (track.kind === 'video') {
      this.remoteVideoTrack = track;
    } else if (track.kind === 'audio') {
      this.remoteAudioTrack = track;
    }
    this.rebuildRemoteMediaStream();
    this.checkReconnectingState();
  }

  private handleRemoteTrackUnsubscribed(track: RemoteTrack) {
    if (track.kind === 'video' && this.remoteVideoTrack?.sid === track.sid) {
      this.remoteVideoTrack = undefined;
    } else if (track.kind === 'audio' && this.remoteAudioTrack?.sid === track.sid) {
      this.remoteAudioTrack = undefined;
    }
    this.rebuildRemoteMediaStream();
    this.checkReconnectingState();
  }

  /**
   * Rebuilds the combined MediaStream from remote video and audio tracks.
   */
  private rebuildRemoteMediaStream() {
    if (typeof document === 'undefined') return;

    const videoTrack = this.remoteVideoTrack?.mediaStreamTrack;
    const audioTrack = this.remoteAudioTrack?.mediaStreamTrack;

    if (!videoTrack || !audioTrack) {
      this.remoteMediaStream = undefined;
      return;
    }

    if (videoTrack.readyState === 'ended' || audioTrack.readyState === 'ended') {
      this.remoteMediaStream = undefined;
      return;
    }

    const stream = new MediaStream();
    stream.addTrack(videoTrack);
    stream.addTrack(audioTrack);

    this.remoteMediaStream = stream;
  }

  private async checkReconnectingState() {
    if (this.room?.state !== 'connected') return;

    const isDisconnected = !this.remoteVideoTrack || !this.remoteAudioTrack;
    const isShowingReconnecting = this.displayTrack?.name === 'reconnecting-overlay';

    if (isDisconnected && !isShowingReconnecting) {
      const reconnectingOverlay = this.createReconnectingOverlayTrack('reconnecting-overlay');
      await this.setDisplayTrack(reconnectingOverlay.track, reconnectingOverlay.stop);
    } else if (!isDisconnected && isShowingReconnecting) {
      await this.setDisplayTrack();
    }
  }

  protected override async onRoomConnected(room: Room): Promise<void> {
    await super.onRoomConnected?.(room);
    await this.setDisplayTrack();

    if (!this.offerSent) {
      while (!this.callManager?.hasRemoteDataTracks()) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      await this.callManager?.offerToStart();
      this.offerSent = true;
      await this.logEvent({
        level: 'info',
        message: 'Automated clinician sent offer to start',
        context: { roomName: room.name }
      });
    }
  }

  protected override async onRoomDisconnected(_room: Room, _error?: Error): Promise<void> {
    this.offerSent = false;
    await super.onRoomDisconnected?.(_room, _error);
  }

  /**
   * Plays a video clip (prompt, thank you, or replay) and publishes it as a display track.
   */
  private async playClip(url: string, label: string, clearOnError = true): Promise<boolean> {
    if (typeof document === 'undefined') return false;
    try {
      const { track, video, stop, audioTrack } = await this.createVideoClipTrack(url, label);
      await this.setDisplayTrack(track, stop, audioTrack);
      if (video) {
        await new Promise<void>((resolve) => {
          video.onended = () => resolve();
          setTimeout(() => resolve(), 30000);
        });
      }
      await this.setDisplayTrack();
      return true;
    } catch (error) {
      await this.logEvent({
        level: 'error',
        message: 'Failed to play clip',
        context: {
          url,
          label,
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        }
      });
      if (clearOnError) {
        await this.setDisplayTrack();
      }
      return false;
    }
  }

  private async waitForTrackLive(track: MediaStreamTrack, timeoutMs = 2000): Promise<void> {
    if (track.readyState === 'live') return;
    await new Promise<void>((resolve) => {
      let resolved = false;
      const settle = () => {
        if (!resolved) {
          resolved = true;
          resolve();
        }
      };
      const timer = setTimeout(() => settle(), timeoutMs);
      const onunmute = () => {
        clearTimeout(timer);
        settle();
      };
      // @ts-ignore - some browsers implement onunmute
      if (typeof (track as any).onunmute === 'function') {
        const orig = (track as any).onunmute;
        (track as any).onunmute = (...args: any[]) => {
          try {
            if (orig) orig.apply(track, args);
          } catch {}
          onunmute();
        };
      }
    });
  }

  /**
   * Starts recording the clinician's A/V using MediaRecorder and captureStream.
   */
  private async startRecording(): Promise<void> {
    if (!this.remoteVideoTrack || !this.remoteAudioTrack) {
      await this.logEvent({
        level: 'warn',
        message: 'Cannot start recording - missing video or audio track',
        context: {}
      });
      return;
    }

    if (typeof document === 'undefined') return;

    try {
      if (this.recorder && this.recorder.state !== 'inactive') {
        try {
          this.recorder.stop();
        } catch {}
      }
      this.recorder = undefined;
      this.cleanupCapturedStream();
      this.cleanupRecordingSourceElement();
      if (this.recordingPlaceholder) {
        this.recordingPlaceholder.stop();
        this.recordingPlaceholder = undefined;
      }
      this.recordedChunks = [];
      this.recordedBlob = undefined;

      this.rebuildRemoteMediaStream();
      if (!this.remoteMediaStream) {
        throw new Error('Remote media stream not available');
      }

      const videoTrack = this.remoteVideoTrack.mediaStreamTrack;
      const audioTrack = this.remoteAudioTrack.mediaStreamTrack;
      await this.waitForTrackLive(videoTrack, 2000);
      await this.waitForTrackLive(audioTrack, 2000);

      const sourceElement = await this.ensureRecordingSourceElement();
      if (!sourceElement) {
        throw new Error('Could not create recording source element');
      }

      const captureStream: MediaStream | null =
        (sourceElement as any).captureStream?.() ?? (sourceElement as any).mozCaptureStream?.() ?? null;

      const recordingStream =
        !captureStream || captureStream.getTracks().length === 0
          ? new MediaStream([videoTrack, audioTrack])
          : captureStream;

      if (!recordingStream.getTracks().some((t) => t.readyState === 'live')) {
        throw new Error('No active tracks available for recording');
      }

      this.capturedStream = recordingStream;

      const mimeType = this.getSupportedMimeType();
      if (!mimeType) {
        await this.logEvent({
          level: 'warn',
          message: 'MediaRecorder not supported in this browser',
          context: {}
        });
        return;
      }
      const recorder = new MediaRecorder(this.capturedStream, {
        mimeType,
        videoBitsPerSecond: 2_500_000,
        audioBitsPerSecond: 128_000
      });

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      recorder.onerror = () => {
        this.logEvent({
          level: 'error',
          message: 'MediaRecorder error',
          context: {}
        }).catch(() => {});
      };

      recorder.start(500);
      this.recorder = recorder;

      this.recordingPlaceholder = this.createTextTrack('Recording…', RECORDING_LABEL);
      await this.setDisplayTrack(this.recordingPlaceholder.track, this.recordingPlaceholder.stop);
      await this.sendRecordingStarted();
    } catch (error) {
      await this.logEvent({
        level: 'error',
        message: 'Failed to start recording',
        context: { error: error instanceof Error ? error.message : String(error) }
      });
    }
  }

  private getSupportedMimeType(): string | null {
    const types = [
      'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm'
    ];
    for (const type of types) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return null;
  }

  /**
   * Ensures a hidden video element exists for recording via captureStream.
   */
  private async ensureRecordingSourceElement(): Promise<HTMLVideoElement | null> {
    if (typeof document === 'undefined') return null;
    if (!this.remoteMediaStream) return null;

    const streamTracks = this.remoteMediaStream.getTracks();
    if (streamTracks.length === 0 || streamTracks.some((t) => t.readyState !== 'live')) {
      return null;
    }

    if (this.recordingSourceElement) {
      try {
        this.recordingSourceElement.pause();
        this.recordingSourceElement.remove();
      } catch {}
      this.recordingSourceElement = undefined;
    }

    const el = document.createElement('video');
    el.autoplay = true;
    el.muted = true;
    el.playsInline = true;
    el.style.position = 'fixed';
    el.style.top = '-9999px';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    this.recordingSourceElement = el;

    el.srcObject = this.remoteMediaStream;

    const isPlaying = await new Promise<boolean>((resolve) => {
      let resolved = false;
      const done = (success: boolean) => {
        if (resolved) return;
        resolved = true;
        resolve(success);
      };

      const timeout = setTimeout(() => done(false), 3000);

      const handleSuccess = () => {
        clearTimeout(timeout);
        done(true);
      };

      el.onloadedmetadata = () => {
        el.play()
          .then(handleSuccess)
          .catch(() => {
            clearTimeout(timeout);
            done(false);
          });
      };

      el.onerror = () => {
        clearTimeout(timeout);
        done(false);
      };
    });

    if (!isPlaying) {
      try {
        el.pause();
        el.remove();
      } catch {}
      this.recordingSourceElement = undefined;
      return null;
    }

    return this.recordingSourceElement;
  }

  private cleanupRecordingSourceElement() {
    if (this.recordingSourceElement) {
      try {
        this.recordingSourceElement.pause();
        this.recordingSourceElement.remove();
      } catch {}
      this.recordingSourceElement = undefined;
    }
  }

  private cleanupCapturedStream() {
    this.capturedStream = undefined;
  }

  /**
   * Stops recording and assembles the final blob.
   */
  private async stopRecording(): Promise<void> {
    if (!this.recorder) {
      Promise.resolve(this.sendRecordingStopped({ hasRecording: false })).catch(() => {});
      return;
    }

    const recorder = this.recorder;
    const mimeType = recorder.mimeType || 'video/webm';

    await new Promise<void>((resolve) => {
      if (recorder.state === 'inactive') {
        this.recordedBlob = this.recordedChunks.length ? new Blob(this.recordedChunks, { type: mimeType }) : undefined;
        resolve();
        return;
      }

      recorder.onstop = () => {
        try {
          this.recordedBlob = this.recordedChunks.length
            ? new Blob(this.recordedChunks, { type: mimeType })
            : undefined;
        } catch {
          this.recordedBlob = undefined;
        }
        resolve();
      };

      try {
        recorder.stop();
      } catch {
        this.recordedBlob = this.recordedChunks.length ? new Blob(this.recordedChunks, { type: mimeType }) : undefined;
        resolve();
      }
    });

    this.recorder = undefined;
    this.cleanupCapturedStream();
    this.cleanupRecordingSourceElement();

    try {
      if (this.recordingPlaceholder) {
        this.recordingPlaceholder.stop();
        this.recordingPlaceholder = undefined;
      }
      const stoppedPlaceholder = this.createTextTrack('Recording stopped', 'recording-stopped');
      await this.setDisplayTrack(stoppedPlaceholder.track, stoppedPlaceholder.stop);
      await new Promise((r) => setTimeout(r, 2000));
      await this.setDisplayTrack();
    } catch {}

    const hasRecording = !!this.recordedBlob && this.recordedBlob.size > 0;
    Promise.resolve(this.sendRecordingStopped({ hasRecording })).catch(() => {});
  }

  /**
   * Replays the recorded blob by playing it as a video track.
   */
  private async replayRecording(): Promise<void> {
    if (!this.recordedBlob) {
      Promise.resolve(this.sendReplayComplete({ hasRecording: false })).catch(() => {});
      return;
    }

    try {
      await this.setDisplayTrack();
    } catch {}

    const url = URL.createObjectURL(this.recordedBlob);
    try {
      const success = await this.playClip(url, 'automated-replay', false);
      if (success) {
        Promise.resolve(this.sendReplayComplete({ hasRecording: true })).catch(() => {});
      } else {
        Promise.resolve(this.sendReplayComplete({ hasRecording: false })).catch(() => {});
      }
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  private async unpublishDisplayTrack() {
    if (this.displayTrack && this.room) {
      try {
        this.room.localParticipant.unpublishTrack(this.displayTrack);
      } catch {}
      try {
        this.displayTrack.stop();
      } catch {}
      this.displayCleanup?.();
      this.displayTrack = undefined;
      this.displayCleanup = undefined;
    }
  }

  private async unpublishDisplayAudioTrack() {
    if (this.displayAudioTrack && this.room) {
      try {
        this.room.localParticipant.unpublishTrack(this.displayAudioTrack);
      } catch {}
      try {
        this.displayAudioTrack.stop();
      } catch {}
      this.displayAudioTrack = undefined;
    }
  }

  /**
   * Publishes or unpublishes display tracks (overlays, clips) to the room.
   */
  private async setDisplayTrack(track?: LocalVideoTrack, cleanup?: () => void, audioTrack?: LocalAudioTrack) {
    await this.unpublishDisplayTrack();
    await this.unpublishDisplayAudioTrack();

    if (track && this.room) {
      await this.room.localParticipant.publishTrack(track);
      this.displayTrack = track;
      this.displayCleanup = cleanup;
    } else if (this.room) {
      this.defaultOverlay ??= this.createBlackOverlayTrack('default-overlay');
      if (this.displayTrack?.name !== 'default-overlay') {
        await this.room.localParticipant.publishTrack(this.defaultOverlay.track);
        this.displayTrack = this.defaultOverlay.track;
        this.displayCleanup = this.defaultOverlay.stop;
      }
    }

    if (audioTrack && this.room) {
      await this.room.localParticipant.publishTrack(audioTrack);
      this.displayAudioTrack = audioTrack;
    }
  }

  /**
   * Creates a video track from a video file by rendering it to a canvas.
   * Also extracts audio if available.
   */
  private async createVideoClipTrack(url: string, label: string): Promise<CanvasTrack> {
    return new Promise<CanvasTrack>((resolve, reject) => {
      if (typeof document === 'undefined') {
        reject(new Error('Document not available'));
        return;
      }

      const video = document.createElement('video');
      video.src = url;
      video.crossOrigin = 'anonymous';
      video.muted = false;
      video.volume = 1;
      video.playsInline = true;
      video.preload = 'auto';

      video.onloadedmetadata = async () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Unable to get canvas context'));
          return;
        }

        let audioTrack: LocalAudioTrack | undefined;
        let audioContext: AudioContext | undefined;

        const AudioContextClass = globalThis.AudioContext || (globalThis as any).webkitAudioContext;
        if (AudioContextClass) {
          try {
            audioContext = new AudioContextClass();
            if (audioContext.state === 'suspended') {
              await audioContext.resume();
            }
            const source = audioContext.createMediaElementSource(video);
            const destination = audioContext.createMediaStreamDestination();
            source.connect(destination);
            const audioTracks = destination.stream.getAudioTracks();
            if (audioTracks.length > 0) {
              audioTracks[0].enabled = true;
              audioTrack = new LocalAudioTrack(audioTracks[0], { name: `${label}-audio` });
              audioTrack.enable();
            }
          } catch {}
        }

        try {
          if (audioContext?.state === 'suspended') {
            await audioContext.resume();
          }
          await video.play();
        } catch (error) {
          if (audioContext && audioContext.state !== 'closed') {
            audioContext.close().catch(() => {});
          }
          reject(error);
          return;
        }

        let rafId = 0;
        let stopped = false;
        const drawFrame = () => {
          if (stopped) return;
          try {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          } catch {}
          rafId = requestAnimationFrame(drawFrame);
        };
        drawFrame();
        const stream = canvas.captureStream(30);
        const [mediaTrack] = stream.getVideoTracks();
        const localTrack = new LocalVideoTrack(mediaTrack, { name: label });

        const cleanup = () => {
          if (stopped) return;
          stopped = true;
          cancelAnimationFrame(rafId);
          video.onended = null;
          try {
            video.pause();
            video.src = '';
            video.load();
          } catch {}
          try {
            const tracks = stream.getTracks();
            for (const track of tracks) {
              track.stop();
            }
          } catch {}
          try {
            localTrack.stop();
          } catch {}
          try {
            audioTrack?.stop();
          } catch {}
          this.closeAudioContext(audioContext);
        };

        video.onended = cleanup;

        resolve({
          track: localTrack,
          video,
          audioTrack,
          stop: cleanup
        });
      };

      video.onerror = () => {
        reject(new Error(`Failed to load video clip: ${url}`));
      };
    });
  }

  /**
   * Creates a text overlay track (e.g., "Recording...").
   */
  private createTextTrack(message: string, label: string): CanvasTrack {
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 360;
    const ctx = canvas.getContext('2d')!;
    let rafId = 0;
    let stopped = false;
    const draw = () => {
      if (stopped) return;
      ctx.fillStyle = '#111';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#fff';
      ctx.font = '32px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(message, canvas.width / 2, canvas.height / 2);
      rafId = requestAnimationFrame(draw);
    };
    draw();
    const stream = canvas.captureStream(10);
    const [mediaTrack] = stream.getVideoTracks();
    const track = new LocalVideoTrack(mediaTrack, { name: label });
    return {
      track,
      stop: () => {
        if (stopped) return;
        stopped = true;
        cancelAnimationFrame(rafId);
        stream.getTracks().forEach((t) => {
          try {
            t.stop();
          } catch {}
        });
        try {
          track.stop();
        } catch {}
      }
    };
  }

  private createBlackOverlayTrack(label: string): CanvasTrack {
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 360;
    const ctx = canvas.getContext('2d')!;
    let rafId = 0;
    let stopped = false;
    const draw = () => {
      if (stopped) return;
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      rafId = requestAnimationFrame(draw);
    };
    draw();
    const stream = canvas.captureStream(10);
    const [mediaTrack] = stream.getVideoTracks();
    const track = new LocalVideoTrack(mediaTrack, { name: label });
    return {
      track,
      stop: () => {
        if (stopped) return;
        stopped = true;
        cancelAnimationFrame(rafId);
        stream.getTracks().forEach((t) => {
          try {
            t.stop();
          } catch {}
        });
        try {
          track.stop();
        } catch {}
      }
    };
  }

  private createReconnectingOverlayTrack(label: string): CanvasTrack {
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 360;
    const ctx = canvas.getContext('2d')!;
    let rafId = 0;
    let stopped = false;
    const draw = () => {
      if (stopped) return;
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      ctx.fillStyle = '#fff';
      ctx.font = '24px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Awaiting Your Test Clinician...', centerX, centerY);
      rafId = requestAnimationFrame(draw);
    };
    draw();
    const stream = canvas.captureStream(10);
    const [mediaTrack] = stream.getVideoTracks();
    const track = new LocalVideoTrack(mediaTrack, { name: label });
    return {
      track,
      stop: () => {
        if (stopped) return;
        stopped = true;
        cancelAnimationFrame(rafId);
        stream.getTracks().forEach((t) => {
          try {
            t.stop();
          } catch {}
        });
        try {
          track.stop();
        } catch {}
      }
    };
  }
}
